'use client';

import Image from '@/components/Image';
import { useAppNavigation } from '@/hooks/useAppNavigation';
import { useStore } from '@/providers/app-store-provider';
import { formatPriceNumberWithSymbol, priceInfo } from '@/utils/price';
import Link from 'next/link';
import { useEffect } from 'react';
import { useInView } from 'react-intersection-observer';
import { usePersonalityFitTracking } from '../hooks/usePersonalityFitTracking';

function ProductItem({ item, index, onClick }: { item: any; index: number; onClick: () => void }) {
  const { trackMktClick, trackMktExp } = usePersonalityFitTracking();
  const { navigate } = useAppNavigation();
  const currency = useStore((s) => s.currency);
  const userInfo = useStore((s) => s.userInfo);

  // 商品卡片曝光
  const [expRef, inView] = useInView({ threshold: 0.5, triggerOnce: true });
  // 按钮曝光
  const [btnExpRef, btnInView] = useInView({ threshold: 0.5, triggerOnce: true });

  // 构建埋点参数
  const buildTrackParams = (mktName: string) => {
    const params: any = { mkt_name: mktName };
    if (Number(item?.jump_type) === 2) {
      // 跳转详情页
      params.shop_sku = item?.shop_sku || '';
      params.business_sku = item?.business_sku || '';
      params.product_sku = item?.jump_value || '';
    } else if (Number(item?.jump_type) === 1) {
      // 跳转列表页
      params.group_id = item?.jump_value || '';
    }
    return params;
  };

  // 商品卡片曝光
  useEffect(() => {
    if (inView) {
      try {
        trackMktExp(buildTrackParams(`Product_${index}`));
      } catch {
        /* noop */
      }
    }
  }, [inView, index, trackMktExp, buildTrackParams]);

  // 按钮曝光
  useEffect(() => {
    if (btnInView && item?.btn_word) {
      try {
        trackMktExp(buildTrackParams(`Product_${index}_Btn`));
      } catch {
        /* noop */
      }
    }
  }, [btnInView, index, item?.btn_word, trackMktExp, buildTrackParams]);

  const href =
    Number(item?.jump_type) === 2
      ? `/goods-detail/${item?.jump_value}`
      : `/goods-list/${item?.jump_value}`;
  const hasId = Boolean(item?.id);
  const pInfo: any = priceInfo(userInfo, item, currency) || {};
  const nowPrice = formatPriceNumberWithSymbol(
    String(pInfo?.sell_price || pInfo?.price || '0'),
    currency,
  );
  const oldPrice =
    pInfo && pInfo.price && pInfo.sell_price && pInfo.sell_price !== pInfo.price
      ? formatPriceNumberWithSymbol(String(pInfo.price), currency)
      : '';
  const aspectRatioStyle = item?.aspectRatio
    ? { aspectRatio: String(item.aspectRatio) }
    : undefined;
  const showGlasses = Boolean(item?.glasses_image);

  return (
    <li className="product-item">
      <Link
        href={href}
        className="block"
        onClick={(e) => {
          e.preventDefault();
          // 列表页跳转需要标记 sessionStorage
          if (
            Number(item?.jump_type) === 1 &&
            typeof window !== 'undefined' &&
            window.sessionStorage
          ) {
            try {
              window.sessionStorage.setItem('zl_goodsListEnterPageScene', '活动专题页');
            } catch {
              /* noop */
            }
          }
          try {
            trackMktClick(buildTrackParams(`Product_${index}`));
          } catch {
            /* noop */
          }
          navigate(href);
          onClick?.();
        }}
      >
        <div
          ref={expRef}
          className="img-wrap relative mt-[1.56vw] rounded-[12px] overflow-hidden"
          style={aspectRatioStyle}
        >
          <Image
            src={item?.image || item?.bg_image?.[0] || ''}
            alt=""
            width={1801}
            height={800}
            className="w-full h-auto"
          />

          {item?.description && (
            <p
              className="word absolute text-white text-[min(1.25vw,14px)] leading-[min(1.563vw,30px)] text-center !text-[18px]"
              style={{ width: '20.94vw', top: '17.29vw', right: '21.88vw' }}
            >
              {item.description}
            </p>
          )}

          {hasId && (
            <div className="price notranslate absolute left-[56.88vw] bottom-[5.42vw] flex items-center">
              <span className="now-price notranslate font-bb text-white text-[1.875vw] leading-[1.875vw]">
                {nowPrice}
              </span>
              {oldPrice && (
                <span className="old-price notranslate ml-[1.25vw] text-white text-[1.25vw] line-through">
                  {oldPrice}
                </span>
              )}
            </div>
          )}

          {item?.btn_word && (
            <div
              ref={btnExpRef}
              className="btn absolute right-[5.05vw] bottom-[4.48vw] rounded-[10px] font-bb text-black text-[1.25vw] px-[2.3vw] py-[17px]"
              style={{ backgroundColor: item?.btn_bg, opacity: Number(item?.btn_opacity || 1) }}
              onClick={(e) => {
                e.stopPropagation();
                // 列表页跳转需要标记 sessionStorage
                if (
                  Number(item?.jump_type) === 1 &&
                  typeof window !== 'undefined' &&
                  window.sessionStorage
                ) {
                  try {
                    window.sessionStorage.setItem('zl_goodsListEnterPageScene', '活动专题页');
                  } catch {
                    /* noop */
                  }
                }
                try {
                  trackMktClick(buildTrackParams(`Product_${index}_Btn`));
                } catch {
                  /* noop */
                }
              }}
            >
              {item?.btn_word}
            </div>
          )}

          {showGlasses && (
            <>
              <Image
                src={`${item.glasses_image}?imbypass=true`}
                alt=""
                width={416}
                height={200}
                className="glasses-image w-[21.67vw] h-auto absolute"
                style={{ bottom: '10.88vw', right: '3.49vw' }}
              />
              <style>{`
                @keyframes pf-float-animation { 0% { transform: translateY(0); } 100% { transform: translateY(-40px); } }
                .glasses-image { animation: pf-float-animation 2s ease-in-out infinite alternate; }
              `}</style>
            </>
          )}
        </div>
      </Link>
    </li>
  );
}

export default function PersonalityProductLists({ productLists }: { productLists: any[] }) {
  if (!Array.isArray(productLists) || productLists.length === 0) return null;

  return (
    <ul className="personality-goods-list px-[3.125vw] mt-[50px]">
      {productLists.map((item: any, index: number) => (
        <ProductItem key={index} item={item} index={index} onClick={() => {}} />
      ))}
    </ul>
  );
}
